import React, { useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { fetchMatterCD, deleteMatterCD } from "../../action/matterCD";
import { setAlert } from "../../action/alert";
import Alert from "../layout/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import GetAppIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import Moment from "react-moment";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import { LabelText } from "../Common/Typography/AppTypography";

const formControlSx = {
  width: "100%",
  maxWidth: { xs: "100%", md: 360 },
};

function handleDownloadHexCertificate(hex, filename = "certificate.txt") {
  const blob = new Blob([hex], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function GetCDForm() {
  const dispatch = useDispatch();
  const [productId, setProductId] = useState("");
  const [fwVersion, setFwVersion] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [fwVersionError, setFwVersionError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState("");

  const handleDelete = async (cd) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this certificate declaration? This action cannot be undone."
      )
    ) {
      return;
    }
    const deleteKey = `${cd._id || cd.id}_${cd._productId}_${cd.fwVersion}_${cd.category}`;
    setDeleteLoading(deleteKey);
    try {
      await deleteMatterCD(cd._id || cd.id, cd._productId, cd.fwVersion);
      dispatch(setAlert("Certificate declaration deleted successfully!", "success"));
      setResults((prev) =>
        prev.filter(
          (item) =>
            `${item._id || item.id}_${item._productId}_${item.fwVersion}_${item.category}` !==
            deleteKey
        )
      );
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message;
      setError(errMsg);
      dispatch(setAlert(errMsg, "error"));
    }
    setDeleteLoading("");
  };

  const resetForm = () => {
    setProductId("");
    setFwVersion("");
    setResults([]);
    setError("");
    setFwVersionError("");
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFwVersionError("");
    setResults([]);
    if (!/^\d+\.\d+$/.test(fwVersion)) {
      setFwVersionError("Firmware Version must be a decimal value (e.g. 1.0, 2.5)");
      return;
    }
    setError("");
    setLoading(true);
    const ids = productId
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    let allResults = [];
    try {
      for (let id of ids) {
        const res = await fetchMatterCD(id, fwVersion);
        if (Array.isArray(res.data)) {
          allResults = allResults.concat(res.data.map((cd) => ({ ...cd, _productId: id })));
        } else {
          allResults.push({ ...res.data, _productId: id });
        }
      }
      setResults(allResults);
      if (allResults.length === 0) {
        dispatch(setAlert("Certificate declaration not found", "danger"));
        setError("");
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message;
      dispatch(setAlert(errMsg, "danger"));
      setError("");
    }
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      <form onSubmit={handleSubmit}>
        {/* Product ID */}
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Product ID:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter Product ID"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        {/* Firmware Version */}
        <Grid2 container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Firmware Version:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter Firmware Version"
              value={fwVersion}
              onChange={(e) => setFwVersion(e.target.value)}
              sx={formControlSx}
            />
            {fwVersionError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {fwVersionError}
              </Typography>
            )}
          </Grid2>
        </Grid2>

        {/* Buttons */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <AppButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={loading}
            loading={loading}
          >
            Get CD
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </AppButton>
        </Box>
      </form>

      <Box sx={{ mt: 2 }}>
        <Alert />
      </Box>

      {results.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {results.map((cd, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{ p: 2, mb: 2, borderRadius: 1, position: "relative", bgcolor: "#fafafa" }}
            >
              <IconButton
                aria-label="delete"
                onClick={() => handleDelete(cd)}
                disabled={deleteLoading === `${cd._id || cd.id}_${cd._productId}_${cd.fwVersion}_${cd.category}`}
                sx={{ position: "absolute", top: 8, right: 8, color: "grey.600" }}
                size="small"
              >
                {deleteLoading === `${cd._id || cd.id}_${cd._productId}_${cd.fwVersion}_${cd.category}` ? (
                  <CircularProgress size={18} />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                    Product ID
                  </Typography>
                  <Typography variant="body2">{cd._productId || cd.productId}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                    Firmware Version
                  </Typography>
                  <Typography variant="body2">{cd.fwVersion}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                    Category
                  </Typography>
                  <Typography variant="body2">{cd.category}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                    Last Updated On
                  </Typography>
                  <Typography variant="body2">
                    <Moment format="MMMM Do YYYY, h:mm:ss a" date={cd.lastUpdatedOn} />
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                  Certificate (CD File)
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {cd.certificateUrl || cd.certificateData ? (
                    <a
                      href={
                        cd.certificateUrl ||
                        `data:application/x-pem-file;base64,${cd.certificateData}`
                      }
                      download={`certificate_${cd._productId || cd.productId}_${cd.fwVersion}.pem`}
                    >
                      <GetAppIcon sx={{ color: "#AFB42B" }} />
                    </a>
                  ) : cd.certificate ? (
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleDownloadHexCertificate(
                          cd.certificate,
                          `certificate_${cd._productId || cd.productId}_${cd.fwVersion}.pem`
                        )
                      }
                      sx={{ p: 0 }}
                    >
                      <GetAppIcon sx={{ color: "#AFB42B" }} />
                    </IconButton>
                  ) : (
                    <Typography variant="body2" color="error">
                      Certificate declaration not found
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default GetCDForm;
